import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  MousePointer2, Hand, Pencil, Shapes, Hash, Type, StickyNote, Table2,
  Download, Plus, Minus, Maximize2, Lock, Unlock, X, Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, Trash2, FileDown, GripHorizontal, Square,
  Circle, Triangle, Diamond, ArrowRight,
} from "lucide-react";
import ToolHeader from "../../Components/ToolHeader";

import "./DiagramTool.css";
import "./diagram/architect.css";
import { DEFAULT_SIZE, clamp, anchorPoint, autoAnchors, pointInBox, orthPath, ShapeBody, isCanvasLine, connectorGeometry } from './diagram/engine';
import { useDiagramDocument } from './diagram/useDiagramDocument';
import { newId as uid, newDiagram, validateDiagram, duplicateSelection, arrangeElements, LIMITS } from './diagram/model';
import { CATALOG } from './diagram/catalog';
import { listDiagrams, deleteDiagram } from './diagram/storage';
import { fromMermaid } from './diagram/mermaid';
import MermaidPanel from './diagram/MermaidPanel';
import { renderMermaid } from './diagram/mermaidRenderer';
import { encodeShare, decodeShare } from './diagram/share';
import { exportDiagram } from './diagram/export';
import { Toolbar, Palette, Properties } from './diagram/Controls';

import "../JSONTools/JSONformatter.css";
import { Row, Col } from "react-bootstrap";
export { getShapeToolFromPickerId } from './diagram/engine';



const SWATCHES = ["#2563eb", "#06b6d4", "#16a34a", "#f59e0b", "#e11d48", "#0f172a"];
export default function Whiteboard() {
  const store = useDiagramDocument();
  const { document: doc, current, change, replace, begin, end, undo, redo, ready, message, setMessage } = store;
  const elements = doc.elements, connectors = doc.connectors;
  const setElements = useCallback(updater => change(d => ({ elements: typeof updater === 'function' ? updater(d.elements) : updater })), [change]);
  const setConnectors = useCallback(updater => change(d => ({ connectors: typeof updater === 'function' ? updater(d.connectors) : updater })), [change]);
  const [selection, setSelection] = useState([]);
  const [grid, setGrid] = useState(true), [snap, setSnap] = useState(true), [rulers, setRulers] = useState(true);
  const [panels, setPanels] = useState({ shapes: true, properties: true });
  const [search, setSearch] = useState(''), [category, setCategory] = useState('General');
  const [mode, setMode] = useState('canvas'), [saved, setSaved] = useState(null), [shareURL, setShareURL] = useState('');
  const fileRef = useRef(null), rootRef = useRef(null), clipboard = useRef(null), frameRef = useRef(null), pendingMove = useRef(null);
  const importTicket = useRef(0);
  const [guide, setGuide] = useState(null);
  const [tool, setTool] = useState("select");
  const [shapeFlyoutOpen, setShapeFlyoutOpen] = useState(false);

  const [exportOpen, setExportOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedConnId, setSelectedConnId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const zoom = doc.viewport.zoom;
  const pan = useMemo(() => ({ x: doc.viewport.x, y: doc.viewport.y }), [doc.viewport.x, doc.viewport.y]);
  const setZoom = useCallback(value => change(d => ({ viewport: { ...d.viewport, zoom: typeof value === 'function' ? value(d.viewport.zoom) : value } }), false), [change]);
  const setPan = useCallback(value => change(d => ({ viewport: { ...d.viewport, ...value } }), false), [change]);
  const [locked, setLocked] = useState(false);
  const [tempConn, setTempConn] = useState(null); // {fromId, fromSide, x, y}
  const [hoveredId, setHoveredId] = useState(null);
  const [penPreview, setPenPreview] = useState(null);
  const toolFav = useMemo(() => ({ toolName: "Flowlens", Iconpath: "/notepad.svg" }), []);

  const outerRef = useRef(null);
  const dragRef = useRef(null);
  const elementsRef = useRef(elements);
  elementsRef.current = elements;

  

  

  const getEl = useCallback((id) => elementsRef.current.find((e) => e.id === id), []);

  const toCanvasCoords = useCallback((clientX, clientY) => {
    try {
      const rect = outerRef.current.getBoundingClientRect();
      return {
        x: (clientX - rect.left - pan.x) / zoom,
        y: (clientY - rect.top - pan.y) / zoom,
      };
    } catch {
      return { x: 0, y: 0 };
    }
  }, [pan, zoom]);

  // ---------- element CRUD ----------
  const addShape = useCallback((type, cx, cy, label = '') => {
    const size = DEFAULT_SIZE[type] || { w: 120, h: 80 };
    const id = uid(type);
    const el = {
      id, type,
      x: snap ? Math.round((cx - size.w / 2) / 20) * 20 : cx - size.w / 2,
      y: snap ? Math.round((cy - size.h / 2) / 20) * 20 : cy - size.h / 2,
      w: size.w, h: size.h,
      text: label,
      fill: "#eff6ff", color: "#0f172a",
      stroke: type === "frame" ? "#4f6df5" : "#5865f2",
    };
    setElements((prev) => (type === "frame" ? [el, ...prev] : [...prev, el]));
    setSelectedId(id);
    setSelection([id]);
    return id;
  }, [setElements, snap]);

  const addTable = useCallback((cx, cy) => {
    const id = uid("table");
    const colWidths = [110, 110];
    const rowHeights = [36, 36];
    const cells = {};
    for (let r = 0; r < rowHeights.length; r++) for (let c = 0; c < colWidths.length; c++) cells[`${r}-${c}`] = "";
    const el = {
      id, type: "table",
      x: cx - 110, y: cy - 36,
      w: colWidths.reduce((a, b) => a + b, 0),
      h: rowHeights.reduce((a, b) => a + b, 0),
      colWidths, rowHeights, cells,
      fill: "#1c1e2b", stroke: "#3a3d54",
    };
    setElements((prev) => [...prev, el]);
    setSelectedId(id);
    setSelection([id]);
  }, [setElements]);

  const addText = useCallback((cx, cy) => {
    const id = uid("text");
    const size = DEFAULT_SIZE.text;
    const el = { id, type: "text", x: cx - size.w / 2, y: cy - size.h / 2, w: size.w, h: size.h, text: "", fill: "transparent", stroke: "transparent", color: "#0f172a", bold: false, italic: false, underline: false, strike: false, align: "left" };
    setElements((prev) => [...prev, el]);
    setSelectedId(id);
    setEditingId(id);
    setSelection([id]);
  }, [setElements]);

  const updateElement = useCallback((id, patch) => {
    setElements((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, [setElements]);

  const deleteSelected = useCallback(() => {
    const ids = new Set(selection.length ? selection : [selectedId]);
    change(d => ({ elements: d.elements.filter(e => !ids.has(e.id)), connectors: d.connectors.filter(c => c.id !== selectedConnId && !ids.has(c.from) && !ids.has(c.to)) }));
    setSelectedId(null); setSelection([]); setSelectedConnId(null);
  }, [selection, selectedId, selectedConnId, change]);
  const clearCanvas = () => { if (window.confirm('Clear this diagram? You can undo this action.')) { change({ elements: [], connectors: [] }); setSelection([]); setSelectedId(null); setSelectedConnId(null); } setExportOpen(false); };
  const selectedIds = selection.length ? selection : selectedId ? [selectedId] : [];
  const addPalette = useCallback((shape, point) => {
    const rect = outerRef.current?.getBoundingClientRect();
    const p = point || toCanvasCoords((rect?.left || 0) + (rect?.width || 600) / 2, (rect?.top || 0) + (rect?.height || 500) / 2);
    const id = addShape(shape.type, p.x, p.y, shape.label);
    setSelection([id]);
  }, [addShape, toCanvasCoords]);
  const replaceDocument = value => {
    importTicket.current++;
    replace(value); setSelection([]); setSelectedId(null); setSelectedConnId(null); setEditingId(null); dragRef.current = null; setTempConn(null); setPenPreview(null); setSaved(null); setShareURL(''); setMode('canvas');
  };
  const runAction = async command => {
    try {
      const ids = new Set(selectedIds);
      if (command === 'save') await store.save();
      else if (command === 'save-as') {
        const name = window.prompt('Save diagram as', doc.name); if (!name?.trim()) return;
        const copy = { ...current.current, id: uid(), name: name.trim().slice(0, 120), createdAt: Date.now(), updatedAt: Date.now() };
        replaceDocument(copy); await store.save();
      } else if (command === 'rename') { const name = window.prompt('Diagram name', doc.name); if (name?.trim()) change({ name: name.trim().slice(0, 120) }); }
      else if (command === 'new') { if (!elements.length || window.confirm('Start a new diagram? Save or export first to keep the current draft.')) replaceDocument(newDiagram()); }
      else if (command === 'open') setSaved(await listDiagrams());
      else if (command === 'delete-saved') { if (window.confirm('Delete this saved diagram from this browser? This cannot be undone.')) { await deleteDiagram(doc.id); replaceDocument(newDiagram()); setMessage('Saved diagram deleted'); } }
      else if (command === 'import') fileRef.current?.click();
      else if (command === 'share') setShareURL(await encodeShare(current.current));
      else if (['json', 'svg', 'png', 'mmd'].includes(command)) await exportDiagram(current.current, command);
      else if (command === 'pdf') exportPDF();
      else if (command === 'fit') fitToScreen();
      else if (command === 'reset-view') change({ viewport: { x: 40, y: 40, zoom: 1 } }, false);
      else if (command === 'delete') deleteSelected();
      else if (command === 'select-all') { setSelection(elements.map(e => e.id)); setSelectedId(elements[0]?.id || null); }
      else if (command === 'copy') { clipboard.current = duplicateSelection(elements, connectors, selectedIds); setMessage('Selection copied in this workspace'); }
      else if (command === 'duplicate' || command === 'paste') {
        const source = command === 'paste' ? clipboard.current : { elements, connectors };
        if (!source) return;
        const copies = duplicateSelection(source.elements, source.connectors, command === 'paste' ? source.elements.map(e => e.id) : selectedIds);
        change(d => ({ elements: [...d.elements, ...copies.elements], connectors: [...d.connectors, ...copies.connectors] }));
        setSelection(copies.elements.map(e => e.id)); setSelectedId(copies.elements[0]?.id || null);
      } else if (command === 'group' || command === 'ungroup') {
        const group = uid();
        setElements(prev => prev.map(e => { if (!ids.has(e.id)) return e; const copy = { ...e }; if (command === 'group') copy.group = group; else delete copy.group; return copy; }));
      } else if (command === 'forward' || command === 'backward') {
        setElements(prev => { const next = [...prev]; if (command === 'forward') { for (let i = next.length - 2; i >= 0; i--) if (ids.has(next[i].id) && !ids.has(next[i + 1].id)) [next[i], next[i + 1]] = [next[i + 1], next[i]]; } else { for (let i = 1; i < next.length; i++) if (ids.has(next[i].id) && !ids.has(next[i - 1].id)) [next[i], next[i - 1]] = [next[i - 1], next[i]]; } return next; });
      } else setElements(prev => arrangeElements(prev, selectedIds, command));
    } catch (error) { setMessage(error.message); }
  };
  const importFile = async event => {
    const file = event.target.files?.[0]; event.target.value = ''; if (!file) return;
    const ticket = ++importTicket.current;
    const snapshot = current.current;
    try {
      if (file.size > LIMITS.bytes) throw new Error('Diagram exceeds 2 MiB.');
      if (!/\.(json|mmd|mermaid|txt)$/i.test(file.name)) throw new Error('Choose a Cobygo JSON or Mermaid text file.');
      const source = await file.text();
      if (ticket !== importTicket.current) return;
      if (!/\.json$/i.test(file.name)) await renderMermaid(source);
      if (ticket !== importTicket.current || current.current !== snapshot) return;
      const next = /\.json$/i.test(file.name) ? validateDiagram(source) : { ...newDiagram(file.name.replace(/\.[^.]+$/, '')), ...fromMermaid(source), mermaid: source };
      if (!current.current.elements.length || window.confirm('Replace the current canvas? Save or export first to keep this draft.')) replaceDocument(next);
    } catch (error) { setMessage(error.message); }
  };
  useEffect(() => {
    if (!ready || !window.location.hash.startsWith('#diagram=')) return;
    let live = true;
    decodeShare(window.location.hash).then(value => { if (!live) return; window.history.replaceState(null, '', window.location.pathname + window.location.search); if (window.confirm('Open shared diagram? Save the current draft first if needed.')) { replace({ ...value, id: uid() }); setMessage('Shared diagram opened locally'); } })
      .catch(error => { if (live) setMessage(error.message); });
    return () => { live = false; };
  }, [ready, replace, setMessage]);

  // ---------- global mouse handling ----------
  useEffect(() => {
    function onMove(e) {
      const drag = dragRef.current;
      if (!drag) return;
      const pt = toCanvasCoords(e.clientX, e.clientY);
      try {
        if (drag.type === "pan") {
          setPan({ x: drag.startPanX + (e.clientX - drag.startClientX), y: drag.startPanY + (e.clientY - drag.startClientY) });
        } else if (drag.type === "move") {
          const dx = pt.x - drag.startX;
          const dy = pt.y - drag.startY;
          const delta = { x: dx, y: dy };
          if (snap) { delta.x = Math.round((drag.origX + dx) / 20) * 20 - drag.origX; delta.y = Math.round((drag.origY + dy) / 20) * 20 - drag.origY; }
          const other = elementsRef.current.find(el => !drag.ids.includes(el.id) && Math.abs(el.x - (drag.origX + delta.x)) < 6 / zoom);
          setGuide(other ? other.x : null);
          if (other && snap) delta.x = other.x - drag.origX;
          setElements(prev => prev.map(el => { const original = drag.originals.find(item => item.id === el.id); return original ? { ...el, x: original.x + delta.x, y: original.y + delta.y } : el; }));
        } else if (drag.type === "resize") {
          const dx = snap ? Math.round((pt.x - drag.startX) / 20) * 20 : pt.x - drag.startX;
          const dy = snap ? Math.round((pt.y - drag.startY) / 20) * 20 : pt.y - drag.startY;
          let { x, y, w, h } = drag.orig;
          if (drag.handle.includes("e")) w = Math.max(30, drag.orig.w + dx);
          if (drag.handle.includes("s")) h = Math.max(24, drag.orig.h + dy);
          if (drag.handle.includes("w")) { w = Math.max(30, drag.orig.w - dx); x = drag.orig.x + dx; }
          if (drag.handle.includes("n")) { h = Math.max(24, drag.orig.h - dy); y = drag.orig.y + dy; }
          updateElement(drag.id, { x, y, w, h });
        } else if (drag.type === "connect") {
          setTempConn({ fromId: drag.fromId, x: pt.x, y: pt.y });
        } else if (drag.type === "pen") {
          if (drag.points.length < LIMITS.points) drag.points.push(pt);
          setPenPreview(drag.points.map((p) => `${p.x},${p.y}`).join(" "));
        } else if (drag.type === "colresize") {
          const el = getEl(drag.id);
          if (!el) return;
          const widths = [...el.colWidths];
          widths[drag.index] = Math.max(40, drag.origWidths[drag.index] + (pt.x - drag.startX));
          updateElement(drag.id, { colWidths: widths, w: widths.reduce((a, b) => a + b, 0) });
        }
      } catch { /* never let a stray drag event crash the app */ }
    }
    function onUp(e) {
      const drag = dragRef.current;
      if (!drag) return;
      try {
        if (drag.type === "connect" || drag.type === "reconnect") {
          const pt = toCanvasCoords(e.clientX, e.clientY);
          const target = [...elementsRef.current].reverse().find((el) => el.id !== drag.fromId && pt.x >= el.x && pt.x <= el.x + el.w && pt.y >= el.y && pt.y <= el.y + el.h);
          if (target) {
            if (drag.type === 'reconnect') setConnectors(prev => prev.map(c => c.id === drag.connId ? { ...c, [drag.endpoint]: target.id, [drag.endpoint + 'Side']: undefined } : c));
            else setConnectors((prev) => [...prev, { id: uid(), from: drag.fromId, to: target.id, fromSide: drag.fromSide, kind: 'elbow' }]);
          }
          setTempConn(null);
        } else if (drag.type === "pen") {
          if (drag.points.length > 1) {
            const xs = drag.points.map((p) => p.x), ys = drag.points.map((p) => p.y);
            const minX = Math.min(...xs), minY = Math.min(...ys);
            const rel = drag.points.map((p) => ({ x: p.x - minX, y: p.y - minY }));
            setElements((prev) => [...prev, {
              id: uid("pen"), type: "pen", x: minX, y: minY,
              w: Math.max(...xs) - minX, h: Math.max(...ys) - minY,
              points: rel, stroke: "#334155",
            }]);
          }
          setPenPreview(null);
        }
      } catch { /* ignore */ }
      dragRef.current = null; setGuide(null); end();
    }
    const move = e => { pendingMove.current = e; if (!frameRef.current) frameRef.current = requestAnimationFrame(() => { frameRef.current = null; onMove(pendingMove.current); }); };
    const up = e => { if (frameRef.current) { cancelAnimationFrame(frameRef.current); frameRef.current = null; onMove(e); } onUp(e); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up); if (frameRef.current) { cancelAnimationFrame(frameRef.current); frameRef.current = null; }
    };
  }, [toCanvasCoords, updateElement, getEl, end, snap, zoom, setElements, setConnectors, setPan]);

  const onWorkspaceKey = e => {
    if (e.target.closest('input,textarea,select,[contenteditable=true]')) return;
    const modifier = e.ctrlKey || e.metaKey, key = e.key.toLowerCase();
    const command = modifier ? { c: 'copy', v: 'paste', d: 'duplicate', a: 'select-all', s: 'save' }[key] : ['Delete', 'Backspace'].includes(e.key) ? 'delete' : null;
    if (modifier && key === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); }
    else if (command) { e.preventDefault(); runAction(command); }
    else if (e.key === 'Escape') { setSelectedId(null); setSelection([]); setSelectedConnId(null); setEditingId(null); setSaved(null); setShareURL(''); }
  };

  // ---------- canvas-level handlers ----------
  const onCanvasMouseDown = (e) => {
    // shapes/connectors/handles call stopPropagation, so this only fires
    // when the empty canvas background itself was clicked.
    setSelectedId(null); setSelection([]);
    setSelectedConnId(null);
    setShapeFlyoutOpen(false);
    setExportOpen(false);
    if (locked || !ready || e.button !== 0) return;
    rootRef.current?.focus({ preventScroll: true });
    const pt = toCanvasCoords(e.clientX, e.clientY);
    if (tool === "pan") {
      dragRef.current = { type: "pan", startClientX: e.clientX, startClientY: e.clientY, startPanX: pan.x, startPanY: pan.y };
    } else if (tool.startsWith("shape:")) {
      const type = tool.split(":")[1];
      addShape(type, pt.x, pt.y);
      setTool("select");
    } else if (tool === "text") {
      addText(pt.x, pt.y);
      setTool("select");
    } else if (tool === "table") {
      addTable(pt.x, pt.y);
      setTool("select");
    } else if (tool === "pen") {
      begin();
      dragRef.current = { type: "pen", points: [pt] };
    }
  };

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom((z) => clamp(+(z - e.deltaY * 0.001).toFixed(2), 0.2, 3));
  };

  const startMove = (e, el) => {
    if (locked || e.button !== 0) return;
    e.stopPropagation();
    if (tool !== "select") return;
    rootRef.current?.focus({ preventScroll: true });
    const groupIds = el.group ? elements.filter(item => item.group === el.group).map(item => item.id) : [el.id];
    const ids = e.shiftKey ? [...new Set([...selectedIds, ...groupIds])] : selectedIds.includes(el.id) ? selectedIds : groupIds;
    setSelection(ids); setSelectedId(el.id);
    setSelectedConnId(null);
    begin();
    const pt = toCanvasCoords(e.clientX, e.clientY);
    dragRef.current = { type: "move", id: el.id, ids, originals: elements.filter(item => ids.includes(item.id)), startX: pt.x, startY: pt.y, origX: el.x, origY: el.y };
  };

  const startResize = (e, el, handle) => {
    if (locked) return;
    e.stopPropagation();
    const pt = toCanvasCoords(e.clientX, e.clientY);
    begin();
    dragRef.current = { type: "resize", id: el.id, handle, startX: pt.x, startY: pt.y, orig: { x: el.x, y: el.y, w: el.w, h: el.h } };
  };

  const startConnect = (e, el, side) => {
    if (locked) return;
    e.stopPropagation();
    const p = anchorPoint(el, side);
    begin();
    dragRef.current = { type: "connect", fromId: el.id, fromSide: side };
    setTempConn({ fromId: el.id, x: p.x, y: p.y });
  };

  const startColResize = (e, el, index) => {
    if (locked) return;
    e.stopPropagation();
    const pt = toCanvasCoords(e.clientX, e.clientY);
    begin();
    dragRef.current = { type: "colresize", id: el.id, index, startX: pt.x, origWidths: [...el.colWidths] };
  };

  const addTableRow = (el) => {
    if (el.rowHeights.length >= 30) return;
    const rowHeights = [...el.rowHeights, 36];
    const cells = { ...el.cells };
    const r = el.rowHeights.length;
    el.colWidths.forEach((_, c) => { cells[`${r}-${c}`] = ""; });
    updateElement(el.id, { rowHeights, cells, h: rowHeights.reduce((a, b) => a + b, 0) });
  };
  const addTableCol = (el) => {
    if (el.colWidths.length >= 30) return;
    const colWidths = [...el.colWidths, 110];
    const cells = { ...el.cells };
    const c = el.colWidths.length;
    el.rowHeights.forEach((_, r) => { cells[`${r}-${c}`] = ""; });
    updateElement(el.id, { colWidths, cells, w: colWidths.reduce((a, b) => a + b, 0) });
  };

  const fitToScreen = () => {
    try {
      if (elements.length === 0) { setZoom(1); setPan({ x: 0, y: 0 }); return; }
      const xs = elements.flatMap((e) => [e.x, e.x + e.w]);
      const ys = elements.flatMap((e) => [e.y, e.y + e.h]);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      const rect = outerRef.current.getBoundingClientRect();
      const padding = 80;
      const scaleX = (rect.width - padding) / Math.max(1, maxX - minX);
      const scaleY = (rect.height - padding) / Math.max(1, maxY - minY);
      const newZoom = clamp(+Math.min(scaleX, scaleY).toFixed(2), 0.2, 2);
      setZoom(newZoom);
      setPan({ x: (rect.width - (maxX - minX) * newZoom) / 2 - minX * newZoom, y: (rect.height - (maxY - minY) * newZoom) / 2 - minY * newZoom });
    } catch { /* no-op */ }
  };

  const exportPDF = () => {
    setExportOpen(false);
    exportDiagram(current.current, 'pdf').catch(error => setMessage(error.message));
  };

  const selectedEl = selectedId ? getEl(selectedId) : null;
  useEffect(() => {
    if (editingId) rootRef.current?.querySelector(`[data-shape-id="${editingId}"] [contenteditable="plaintext-only"]`)?.focus();
  }, [editingId]);

  const HINTS = {
    select: "Click a shape to select it. Drag from a node edge to connect. Double-click any node to edit text.",
    pan: "Drag anywhere on the canvas to pan around.",
    pen: "Click and drag on the canvas to draw freehand.",
    text: "Click on the canvas to drop a text box.",
    table: "Click on the canvas to insert a table.",
    "shape:rectangle": "Click on the canvas to place a rectangle.",
    "shape:ellipse": "Click on the canvas to place an ellipse.",
    "shape:diamond": "Click on the canvas to place a diamond.",
    "shape:triangle": "Click on the canvas to place a triangle.",
    "shape:arrowline": "Click on the canvas to place an arrow.",
    "shape:frame": "Click on the canvas to place a frame.",
  };

  if (!ready) return <p role="status">Opening local diagram workspace…</p>;
  return (
    <Col ref={rootRef} tabIndex={-1} className="da-workspace" onKeyDown={onWorkspaceKey}>
      <ToolHeader title="Flow Lens" description="Design, visualize and share diagrams in one workspace." toolFav={toolFav} />
      <Toolbar action={runAction} doc={doc} change={change} undo={undo} redo={redo} canUndo={store.canUndo} canRedo={store.canRedo} zoom={zoom} setZoom={setZoom} grid={grid} setGrid={setGrid} snap={snap} setSnap={setSnap} rulers={rulers} setRulers={setRulers} mode={mode} setMode={setMode} panels={panels} setPanels={setPanels} />
      <input ref={fileRef} type="file" aria-label="Import diagram file" hidden accept=".json,.mmd,.mermaid,.txt" onChange={importFile} />
      <p className="da-status" role="status">{message} · Browser-local diagrams, no cloud sync</p>
      {saved && <section className="da-dialog" aria-label="Saved diagrams"><h3>Saved diagrams on this browser</h3><button onClick={() => setSaved(null)}>Close saved diagrams</button>{!saved.length && <p>No saved diagrams.</p>}{saved.map(d => <button key={d.id} onClick={() => { if (!elements.length || window.confirm('Open this diagram and replace the current draft?')) replaceDocument(d); }}>{d.name} · {new Date(d.updatedAt).toLocaleString()}</button>)}</section>}
      {shareURL && <section className="da-dialog" aria-label="Share diagram"><h3>Share diagram</h3><p>This link contains the diagram. Anyone with the link can open it. It is not a cloud save. Do not share sensitive diagrams publicly.</p><textarea aria-label="Diagram share URL" readOnly value={shareURL} /><button onClick={() => navigator.clipboard.writeText(shareURL).then(() => setMessage('Share link copied')).catch(() => setMessage('Select and copy the link manually'))}>Copy share link</button><button onClick={() => setShareURL('')}>Close share</button></section>}
      {mode === 'mermaid' && <MermaidPanel doc={doc} change={change} report={setMessage} apply={result => { change(result); setSelection([]); setSelectedId(null); setSelectedConnId(null); setMode('canvas'); setMessage('Mermaid rendered as editable shapes'); }} />}
      <Row >
        <Col className="wb-Instructions">
          <p>Drag shapes or click to add. Shift-click for multi-select. Drag edge dots to connect. Double-click to edit text.</p>
        </Col>
      </Row>
      <div className="da-layout">
        {panels.shapes && <Palette search={search} setSearch={setSearch} category={category} setCategory={setCategory} add={addPalette} />}
        <div className="dt-root">
          <div className="dt-hint">{HINTS[tool] || "Press Delete to remove selected items."}</div>

          <div
            ref={outerRef}
            className="dt-canvas-outer" role="region" aria-label="Diagram canvas"
            style={{ cursor: tool === "pan" ? "grab" : tool === "select" ? "default" : "crosshair", backgroundColor: doc.background, backgroundImage: grid ? undefined : 'none', backgroundSize: `${20 * zoom}px ${20 * zoom}px`, backgroundPosition: `${pan.x}px ${pan.y}px` }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); if (!ready) return; const shape = CATALOG.find(s => s.id === e.dataTransfer.getData('application/x-cobygo-shape')); if (shape) addPalette(shape, toCanvasCoords(e.clientX, e.clientY)); }}
            onMouseDown={onCanvasMouseDown}
            onWheel={onWheel}
          >
            {rulers && <><div className="da-ruler da-ruler-top" aria-hidden="true">{Array.from({ length: 30 }, (_, i) => <span key={i} style={{ left: i * 100 }}>{Math.round((i * 100 - pan.x) / zoom)}</span>)}</div><div className="da-ruler da-ruler-left" aria-hidden="true">{Array.from({ length: 20 }, (_, i) => <span key={i} style={{ top: i * 100 }}>{Math.round((i * 100 - pan.y) / zoom)}</span>)}</div></>}
            <div className="dt-world" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, width: 4000, height: 3000 }}>
              {/* connectors + pen layer */}
              <svg width={4000} height={3000} style={{ position: "absolute", top: 0, left: 0, zIndex: selectedConnId ? 30 : 0, pointerEvents: "none", overflow: "visible" }}>
                <defs>
                  <marker id="dt-arrow" markerWidth="9" markerHeight="9" refX="7" refY="4" orient="auto-start-reverse">
                    <path d="M0,0 L8,4 L0,8 Z" fill="#8b93ff" />
                  </marker>
                </defs>
                {connectors.map((c) => {
                  const a = getEl(c.from), b = getEl(c.to);
                  if (!a || !b) return null;
                  const { p1, p2, d } = connectorGeometry(c, a, b);
                  const isSel = c.id === selectedConnId;
                  return (
                    <g key={c.id} data-connector-id={c.id} style={{ pointerEvents: "stroke" }} onMouseDown={(e) => { e.stopPropagation(); setSelectedConnId(c.id); setSelectedId(null); setSelection([]); }}>
                      <path d={d} fill="none" stroke="transparent" strokeWidth={14} />
                      <path d={d} fill="none" stroke={isSel ? "#ff5c8a" : "#8b93ff"} strokeWidth={c.lineStyle === 'thick' ? 4 : isSel ? 2.5 : 2} strokeDasharray={c.lineStyle === 'dotted' ? '3 4' : undefined} markerStart={c.arrowStart ? "url(#dt-arrow)" : undefined} markerEnd={c.arrowEnd === false ? undefined : "url(#dt-arrow)"} />
                      <text x={(p1.x + p2.x) / 2} y={(p1.y + p2.y) / 2 - 8} fill="#334155" fontSize={13} textAnchor="middle">{c.text}</text>
                      {isSel && [['from', p1], ['to', p2]].map(([endpoint, point]) => <circle key={endpoint} aria-label={`Reconnect ${endpoint}`} cx={point.x} cy={point.y} r={7} fill="#fff" stroke="#2563eb" style={{ pointerEvents: 'all', cursor: 'grab' }} onMouseDown={event => { event.stopPropagation(); begin(); dragRef.current = { type: 'reconnect', connId: c.id, endpoint, fromId: c[endpoint === 'from' ? 'to' : 'from'] }; }} />)}
                    </g>
                  );
                })}
                {tempConn && (() => {
                  const a = getEl(tempConn.fromId);
                  if (!a) return null;
                  const { p1, sideA } = autoAnchors(a, { x: tempConn.x, y: tempConn.y, w: 0, h: 0 });
                  return <path d={orthPath(p1, sideA, { x: tempConn.x, y: tempConn.y })} fill="none" stroke="#8b93ff" strokeDasharray="5 4" strokeWidth={2} />;
                })()}
                {guide !== null && <line x1={guide} x2={guide} y1={-100000} y2={100000} stroke="#ec4899" strokeDasharray="5 5" />}
                {penPreview && <polyline points={penPreview} fill="none" stroke="#334155" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />}
                {elements.filter((e) => e.type === "pen").map((e) => (
                  <polyline key={e.id} points={e.points.map((p) => `${p.x + e.x},${p.y + e.y}`).join(" ")} fill="none"
                    stroke={e.id === selectedId ? "#ff5c8a" : e.stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                    style={{ pointerEvents: "stroke" }} onMouseDown={(ev) => startMove(ev, e)} />
                ))}
                {elements.filter(isCanvasLine).map((e) => {
                  const horizontal = e.type === "lineElement" || e.type === "arrowline" || e.type === "arrowlineLeft";
                  const x1 = e.type === "arrowlineLeft" ? e.x + e.w : e.x + (horizontal ? 0 : e.w / 2);
                  const y1 = e.type === "arrowlineUp" ? e.y + e.h : e.y + (horizontal ? e.h / 2 : 0);
                  const x2 = e.type === "arrowlineLeft" ? e.x : e.x + (horizontal ? e.w : e.w / 2);
                  const y2 = e.type === "arrowlineUp" ? e.y : e.y + (horizontal ? e.h / 2 : e.h);
                  const markerEnd = e.type === "lineElement" ? undefined : "url(#dt-arrow)";
                  return (
                    <g key={e.id} style={{ pointerEvents: "stroke" }} onMouseDown={(ev) => startMove(ev, e)}>
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={14} />
                      <line x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={e.id === selectedId ? "#ff5c8a" : "#8b93ff"} strokeWidth={1} markerEnd={markerEnd} />
                    </g>
                  );
                })}
              </svg>

              {/* shapes / text / table / frame layer */}
              {elements.filter((e) => e.type !== "pen" && !isCanvasLine(e)).map((el) => {
                const isSel = selectedIds.includes(el.id);
                const isEditing = editingId === el.id;
                return (
                  <div key={el.id} data-shape-id={el.id} data-shape-type={el.type}
                    className="dt-shape"
                    style={{ left: el.x, top: el.y, width: el.w, height: el.h, zIndex: el.type === "frame" ? 1 : 10, transform: `rotate(${el.rotation || 0}deg)`, opacity: el.opacity ?? 1, outline: isSel ? "1px dashed #2563eb" : undefined }}
                    onMouseDown={(e) => startMove(e, el)}
                    onMouseEnter={() => setHoveredId(el.id)}
                    onMouseLeave={() => setHoveredId((h) => (h === el.id ? null : h))}
                    onDoubleClick={(e) => { e.stopPropagation(); if (el.type !== "table") setEditingId(el.id); }}
                  >
                    {el.type === "table" ? (
                      <div style={{ width: "100%", height: "100%", position: "relative" }}>
                        <div className="dt-table-handle" title="Drag to move table" onMouseDown={(e) => startMove(e, el)}>
                          <GripHorizontal size={11} color="#6b7086" />
                        </div>
                        <div style={{
                          width: "100%", height: "100%", display: "grid",
                          gridTemplateColumns: el.colWidths.map((w) => `${w}px`).join(" "),
                          gridTemplateRows: el.rowHeights.map((h) => `${h}px`).join(" "),
                          background: "transparent", border: `1px solid ${isSel ? "#4f6df5" : el.stroke}`, borderRadius: 6, overflow: "hidden"
                        }}>
                          {el.rowHeights.map((_, r) => el.colWidths.map((_, c) => (
                            <div key={`${r}-${c}`} className="dt-table-cell" contentEditable="plaintext-only" suppressContentEditableWarning
                              onMouseDown={(e) => e.stopPropagation()}
                              onBlur={(e) => updateElement(el.id, { cells: { ...el.cells, [`${r}-${c}`]: e.currentTarget.textContent } })}>
                              {el.cells[`${r}-${c}`]}
                            </div>
                          )))}
                          {el.colWidths.map((_, c) => c < el.colWidths.length - 1 && (
                            <div key={`cr-${c}`} className="dt-col-resizer" onMouseDown={(e) => startColResize(e, el, c)}
                              style={{ left: el.colWidths.slice(0, c + 1).reduce((a, b) => a + b, 0) - 3, height: el.h }} />
                          ))}
                        </div>
                      </div>
                    ) : el.type === "text" ? (
                      <div className={`dt-label ${isEditing ? "editing" : ""}`}
                        contentEditable={isEditing ? 'plaintext-only' : false} suppressContentEditableWarning
                        onMouseDown={(e) => { if (isEditing) e.stopPropagation(); }}
                        onBlur={(e) => { updateElement(el.id, { text: e.currentTarget.textContent.slice(0, LIMITS.text) }); setEditingId(null); }}
                        style={{
                          color: el.color, fontSize: el.fontSize || 14, fontWeight: el.bold ? 700 : 400, fontStyle: el.italic ? "italic" : "normal",
                          textDecoration: [el.underline && "underline", el.strike && "line-through"].filter(Boolean).join(" "),
                          justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start"
                        }}>
                        {el.text || (isEditing ? "" : "Text")}
                      </div>
                    ) : el.type === "frame" ? (
                      <div style={{ width: "100%", height: "100%", background: el.fill || "transparent", border: `${el.strokeWidth || 1}px dashed ${isSel ? "#4f6df5" : el.stroke}`, borderRadius: el.radius || 8, position: "relative" }}>
                        <div style={{ position: "absolute", top: -22, left: 0, fontSize: 11, color: "#6b7086" }} contentEditable={isEditing ? 'plaintext-only' : false} suppressContentEditableWarning
                          onMouseDown={(e) => isEditing && e.stopPropagation()}
                          onBlur={(e) => { updateElement(el.id, { text: e.currentTarget.textContent.slice(0, LIMITS.text) }); setEditingId(null); }}>
                          {el.text || "Frame"}
                        </div>
                      </div>
                    ) : (
                      <div className="dt-shape-inner">
                        <ShapeBody el={el} />
                        <div className={`dt-label ${isEditing ? "editing" : ""}`} style={{ color: el.color || '#0f172a', fontSize: el.fontSize || 14, fontWeight: el.bold ? 700 : 400, textAlign: el.align || 'center', justifyContent: el.align === 'left' ? 'flex-start' : el.align === 'right' ? 'flex-end' : 'center', whiteSpace: 'pre-wrap' }} contentEditable={isEditing ? 'plaintext-only' : false} suppressContentEditableWarning
                          onMouseDown={(e) => { if (isEditing) e.stopPropagation(); }}
                          onBlur={(e) => { updateElement(el.id, { text: e.currentTarget.textContent.slice(0, LIMITS.text) }); setEditingId(null); }}>
                          {el.text}
                        </div>
                      </div>
                    )}

                    {/* resize handles */}
                    {isSel && !locked && el.type !== "pen" && ["nw", "ne", "sw", "se"].map((h) => {
                      const p = pointInBox({ ...el, x: 0, y: 0 }, h.includes("w") ? 0 : 1, h.includes("n") ? 0 : 1);
                      return (
                        <div key={h} className="dt-handle"
                          style={{
                            left: p.x - 5, top: p.y - 5,
                            cursor: (h === "nw" || h === "se") ? "nwse-resize" : "nesw-resize",
                          }}
                          onMouseDown={(e) => startResize(e, el, h)} />
                      );
                    })}

                    {/* connector handles */}
                    {(isSel || hoveredId === el.id) && !locked && el.type !== "text" && el.type !== "frame" && ["left", "right", "top", "bottom"].map((side) => {
                      const p = anchorPoint({ ...el, x: 0, y: 0, rotation: 0 }, side);
                      return (
                        <div key={side} data-side={side} className="dt-conn-handle" style={{ left: p.x - 6, top: p.y - 6 }}
                          onMouseDown={(e) => startConnect(e, el, side)} />
                      );
                    })}

                    {/* table add row/col */}
                    {el.type === "table" && isSel && !locked && (
                      <>
                        <div className="dt-table-add" style={{ left: el.w + 8, top: el.h / 2 - 10 }} onMouseDown={(e) => e.stopPropagation()} onClick={() => addTableCol(el)}>+</div>
                        <div className="dt-table-add" style={{ left: el.w / 2 - 10, top: el.h + 8 }} onMouseDown={(e) => e.stopPropagation()} onClick={() => addTableRow(el)}>+</div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* floating color row above selected shape */}
          {selectedEl && !locked && selectedEl.type !== "table" && selectedEl.type !== "pen" && !isCanvasLine(selectedEl) && editingId !== selectedEl.id && (
            <div className="dt-color-row" style={{
              left: clamp(selectedEl.x * zoom + pan.x, 8, 9999), top: Math.max(8, selectedEl.y * zoom + pan.y - 42),
            }}>
              {SWATCHES.map((c) => (
                <div key={c} className="dt-swatch" style={{ background: c }}
                  onClick={() => updateElement(selectedEl.id, selectedEl.type === "text" ? { color: c } : { stroke: c })} />
              ))}
              <button className="dt-export-item danger" style={{ padding: "0 4px" }} onClick={deleteSelected} title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          )}

          {/* text formatting toolbar */}
          {editingId && getEl(editingId) && getEl(editingId).type === "text" && (() => {
            const el = getEl(editingId);
            return (
              <div className="dt-text-toolbar" style={{ left: Math.max(8, el.x * zoom + pan.x), top: Math.max(8, el.y * zoom + pan.y - 44) }}>
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { bold: !el.bold })}><Bold size={15} /></button>
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { italic: !el.italic })}><Italic size={15} /></button>
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { underline: !el.underline })}><Underline size={15} /></button>
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { strike: !el.strike })}><Strikethrough size={15} /></button>
                <div className="dt-sep" />
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { align: "left" })}><AlignLeft size={15} /></button>
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { align: "center" })}><AlignCenter size={15} /></button>
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()} onClick={() => updateElement(el.id, { align: "right" })}><AlignRight size={15} /></button>
                <div className="dt-sep" />
                <button className="dt-btn" style={{ width: 30, height: 30 }} onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { updateElement(el.id, { text: (document.activeElement && document.activeElement.textContent) || el.text }); setEditingId(null); }}>
                  <X size={15} />
                </button>
              </div>
            );
          })()}

          {/* left toolbar: zoom / fit / lock */}
          <div className="dt-left-toolbar">
            <button className="dt-btn" onClick={() => setZoom((z) => clamp(+(z + 0.1).toFixed(2), 0.2, 3))} title="Zoom in"><Plus size={17} /></button>
            <button className="dt-btn" onClick={() => setZoom((z) => clamp(+(z - 0.1).toFixed(2), 0.2, 3))} title="Zoom out"><Minus size={17} /></button>
            <button className="dt-btn" onClick={fitToScreen} title="Fit to screen"><Maximize2 size={16} /></button>
            <button className={`dt-btn ${locked ? "active" : ""}`} onClick={() => setLocked((l) => !l)} title={locked ? "Unlock canvas" : "Lock canvas"}>
              {locked ? <Lock size={16} /> : <Unlock size={16} />}
            </button>
          </div>

          {/* bottom main toolbar */}
          <div className="dt-toolbar">
            <button className={`dt-btn ${tool === "select" ? "active" : ""}`} onClick={() => { setTool("select"); setShapeFlyoutOpen(false); }} title="Select"><MousePointer2 size={18} /></button>
            <button className={`dt-btn ${tool === "pan" ? "active" : ""}`} onClick={() => { setTool("pan"); setShapeFlyoutOpen(false); }} title="Pan"><Hand size={18} /></button>
            <button className={`dt-btn ${tool === "pen" ? "active" : ""}`} onClick={() => { setTool("pen"); setShapeFlyoutOpen(false); }} title="Draw"><Pencil size={18} /></button>

            <div style={{ position: "relative" }}>
              <button className={`dt-btn ${tool.startsWith("shape:") ? "active" : ""}`} onClick={() => { setShapeFlyoutOpen((v) => !v);  }} title="Shapes"><Shapes size={18} /></button>
              {shapeFlyoutOpen && (
                <div className="dt-flyout">
                  <button className={`dt-btn ${tool === "shape:rectangle" ? "active" : ""}`} onClick={() => { setTool("shape:rectangle"); setShapeFlyoutOpen(false); }} title="Rectangle"><Square size={18} /></button>
                  <button className={`dt-btn ${tool === "shape:ellipse" ? "active" : ""}`} onClick={() => { setTool("shape:ellipse"); setShapeFlyoutOpen(false); }} title="Ellipse"><Circle size={18} /></button>
                  <button className={`dt-btn ${tool === "shape:diamond" ? "active" : ""}`} onClick={() => { setTool("shape:diamond"); setShapeFlyoutOpen(false); }} title="Diamond"><Diamond size={18} /></button>
                  <button className={`dt-btn ${tool === "shape:triangle" ? "active" : ""}`} onClick={() => { setTool("shape:triangle"); setShapeFlyoutOpen(false); }} title="Triangle"><Triangle size={18} /></button>
                  <button className={`dt-btn ${tool === "shape:arrowline" ? "active" : ""}`} onClick={() => { setTool("shape:arrowline"); setShapeFlyoutOpen(false); }} title="Arrow"><ArrowRight size={18} /></button>
                  <button className="ExecuteButton" onClick={() => { setPanels(p => ({ ...p, shapes: true })); setShapeFlyoutOpen(false); }} title="More shapes">Shape library</button>
                </div>
              )}

            </div>

            <button className={`dt-btn ${tool === "connect-info" ? "active" : ""}`} title="Connector — drag from a node's edge dot" onClick={() => setTool("select")}><Hash size={18} /></button>
            <button className={`dt-btn ${tool === "text" ? "active" : ""}`} onClick={() => { setTool("text"); setShapeFlyoutOpen(false); }} title="Text"><Type size={18} /></button>
            <button className={`dt-btn ${tool === "shape:frame" ? "active" : ""}`} onClick={() => { setTool("shape:frame"); setShapeFlyoutOpen(false); }} title="Frame / note"><StickyNote size={18} /></button>
            <button className={`dt-btn ${tool === "table" ? "active" : ""}`} onClick={() => { setTool("table"); setShapeFlyoutOpen(false); }} title="Table"><Table2 size={18} /></button>

            <div className="dt-sep" />
            <div style={{ position: "relative" }}>
              <button className="dt-btn" onClick={() => setExportOpen((v) => !v)} title="Export"><Download size={18} /></button>
              {exportOpen && (
                <div className="dt-export-menu">
                  <button className="dt-export-item" onClick={exportPDF}><FileDown size={15} /> Export as PDF</button>
                  <button className="dt-export-item danger" onClick={clearCanvas}><Trash2 size={15} /> Clear canvas</button>
                </div>
              )}
            </div>
          </div>
        </div>
        {panels.properties && <Properties selected={selectedEl} connector={connectors.find(c => c.id === selectedConnId)} update={patch => setElements(prev => prev.map(e => selectedIds.includes(e.id) ? { ...e, ...patch } : e))} updateConnector={patch => setConnectors(prev => prev.map(c => c.id === selectedConnId ? { ...c, ...patch } : c))} doc={doc} change={change} />}
      </div>
      <footer className="da-footer">{Math.round(zoom * 100)}% · Grid {grid ? 'on' : 'off'} · Snap {snap ? 'on' : 'off'} · {elements.length} shapes · {connectors.length} connectors · {selectedIds.length} selected</footer>
    </Col >
  );
}
